import { TRIGGER_BASE_URL } from "@qendo/medical";
import { MedicalType } from "@qendo/medical/types";
import { OptionType } from "@qendo/types";
import { router } from "expo-router";
import { useEffect, useId, useState } from "react";

import { Button } from "~/components/Button";
import { OptionSelector } from "~/components/form/combo-box/OptionSelector";
import { FieldGroup } from "~/components/form/FieldGroup";
import { MediaPicker } from "~/components/form/gallery/MediaPicker";
import { RadioSelector } from "~/components/form/radio/RadioSelector";
import { TextArea } from "~/components/form/TextArea";
import { FullPageModal, type FullPageModalProps } from "~/components/layout/modal/FullPageModal";
import { Space } from "~/constants/sizing";
import type { Trigger } from "~/entities/medical/trigger";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useMedical } from "~/providers/MedicalProvider";
import { elementsMatch } from "~/util/array";

type TriggerFormProps = {
    readonly trigger?: Trigger;
    onRequestClose: () => void;
} & FullPageModalProps;

export const TriggerForm = ({ trigger, onRequestClose, ...modalProps }: TriggerFormProps) => {
    const { canUpdateMedical, storeMedical } = useMedical();
    const { saving, setSaving } = useFeedback();
    const [option, setOption] = useState<string | null>(trigger?.option_id ?? null);
    const [description, setDescription] = useState(trigger?.description ?? "");
    const [known, setKnown] = useState(trigger?.known ?? true);
    const [photographs, setPhotographs] = useState<string[]>(trigger?.photograph_ids ?? []);
    const [dirty, setDirty] = useState(false);
    const id = useId();

    const canSave = canUpdateMedical(MedicalType.trigger) && !saving && option !== null;

    useEffect(() => {
        setDirty(
            (isDirty) =>
                isDirty ||
                option !== (trigger?.option_id ?? null) ||
                known !== (trigger?.known ?? true) ||
                description !== (trigger?.description ?? "") ||
                !elementsMatch(photographs, trigger?.photograph_ids ?? []),
        );
    }, [trigger, option, description, known, photographs]);

    const doSave = () => {
        if (!canSave) {
            return;
        }

        setSaving(id);
    };

    const commit = () => {
        storeMedical({
            type: MedicalType.trigger,
            id: trigger?.id,
            option_id: option,
            photograph_ids: photographs,
            description,
            known,
        })
            .then((id) => {
                id &&
                    setTimeout(
                        () => router.dismissTo({ pathname: `${TRIGGER_BASE_URL}/[id]/show`, params: { id } }),
                        10,
                    );
            })
            .finally(() => setSaving(false));
    };

    const is_saving = saving === id;

    // biome-ignore lint/correctness/useExhaustiveDependencies(commit): callback
    useEffect(() => {
        is_saving && commit();
    }, [is_saving]);

    return (
        <FullPageModal
            {...modalProps}
            dirty={dirty}
            title={`${trigger?.id ? "Edit" : "Add"} trigger`}
            cancelable
            headerRight={() => (
                <Button disabled={!canSave} title="Save" size="sm" onPress={doSave} loading={is_saving} />
            )}
            onRequestClose={onRequestClose}
            gap={Space.lg}
        >
            <FieldGroup>
                <OptionSelector
                    type={OptionType.triggers}
                    value={option}
                    onItemSelected={setOption}
                    label="Trigger"
                    disabled={is_saving}
                    name="option_id"
                />
                <RadioSelector
                    title="Is this a known or suspected trigger?"
                    options={[
                        { value: "known", label: "Known" },
                        { value: "suspected", label: "Suspected" },
                    ]}
                    disabled={is_saving}
                    value={known ? "known" : "suspected"}
                    onValueChange={(v: string) => setKnown(v === "known")}
                    columns={2}
                />
            </FieldGroup>
            <FieldGroup>
                <TextArea
                    value={description}
                    onChangeText={setDescription}
                    label="Description"
                    optional
                    disabled={is_saving}
                    name="description"
                />
                <MediaPicker value={photographs} onSelectionChanged={setPhotographs} disabled={is_saving} />
            </FieldGroup>
        </FullPageModal>
    );
};
